import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { ReactComponent as SwitchGreenSvg } from '@assets/svg/switch-green.svg';
import { ReactComponent as SwitchRedSvg } from '@assets/svg/switch-red.svg';
import CollapsibleButton from '@components/CollapsibleButton/CollapsibleButton';
import { ConfigProvider, theme } from 'antd';

import './ActionButton.scss';

type Preset = 'view' | 'edit' | 'disable' | 'delete' | 'enable';

interface ActionButtonType {
  preset?: Preset;
  color?: 'red' | 'green' | undefined;
  children?: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  icon?: React.ReactNode | undefined;
  disabled?: boolean | undefined;
}

interface PresetValues {
  color: string | null;
  icon: React.ReactNode | null;
  text: string | null;
}
const { useToken } = theme;

function getColorValue(
  presetValues: PresetValues,
  colorProp: string | undefined,
  token: any,
) {
  const priorityColor = colorProp || presetValues.color;

  let colorCode: string;
  switch (priorityColor) {
    case 'green':
      colorCode = token.colorSuccess;
      break;
    case 'red':
      colorCode = token.colorError;
      break;
    default:
      colorCode = token.colorInfo;
  }
  return colorCode;
}

function ActionButton({
  color = undefined,
  children = undefined,
  onClick,
  icon = undefined,
  preset = undefined,
  disabled = false,
}: ActionButtonType) {
  if (!preset && !children) {
    throw new Error('操作按鈕必須有 preset 或傳入文字內容');
  }

  const { token } = useToken();

  const { t } = useTranslation();

  function getPresetValues(presetValue: Preset | undefined): PresetValues {
    switch (presetValue) {
      case 'view':
        return {
          color: 'blue',
          icon: <InfoCircleOutlined />,
          text: t('Common.Details'),
        };

      case 'edit':
        return {
          color: 'blue',
          icon: <EditOutlined />,
          text: t('Common.Edit'),
        };
      case 'disable':
        return {
          color: 'red',
          icon: <SwitchRedSvg />,
          text: t('Common.Disable'),
        };
      case 'delete':
        return {
          color: 'red',
          icon: <DeleteOutlined />,
          text: t('Common.Delete'),
        };
      case 'enable':
        return {
          color: 'green',
          icon: <SwitchGreenSvg />,
          text: t('Common.Enable'),
        };

      default:
        return {
          color: null,
          icon: null,
          text: null,
        };
    }
  }

  const presetValues = getPresetValues(preset);
  const colorCode = getColorValue(presetValues, color, token);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorLink: colorCode,
        },
      }}
    >
      <CollapsibleButton
        onClick={onClick}
        className="ActionButton"
        type="link"
        icon={icon || presetValues.icon}
        shape="round"
        disabled={disabled}
      >
        {children || presetValues.text}
      </CollapsibleButton>
    </ConfigProvider>
  );
}

export default ActionButton;
